/*
 * What a send is doing, said the same way for a reply and a forward, and always naming which it is:
 * the ten-second hold with Undo, and the outcome nobody knows ("Check Bison, don't resend")
 * with the way to Bison and, once LeadCue stops looking, the person's answer (`settle-send.tsx`).
 * A held forward under a folded reply box once read "Sending to <lead>", and its unknown outcome
 * "this reply": both read the kind from the send itself, never from the box that shows it.
 */
import { useEffect, useRef } from "react";
import { appCount, appDateTime } from "../../src/app-dates.ts";
import type { InboxSendStatus, SendCheck } from "../../src/inbox-v2/contract.ts";
import { ExternalLinkIcon } from "../icons.tsx";
import { useSecondsLeft } from "./held-sends.tsx";
import { type Settle, SettleSend } from "./settle-send.tsx";

type Send = NonNullable<InboxSendStatus["send"]>;

/** Whether this send is a forward, and so to whom it goes, rather than a reply to the lead. */
export const sendKind = (send: Send | null | undefined): "reply" | "forward" =>
  send?.forwardTo ? "forward" : "reply";

/** Every address a forward goes to: all of To, then Cc and Bcc by count. */
export function forwardAudience(send: Send): string {
  const to = send.recipients?.to ?? (send.forwardTo ? [send.forwardTo] : []);
  const cc = send.recipients?.cc.length ?? 0;
  const bcc = send.recipients?.bcc.length ?? 0;
  return [to.join(", "), cc ? `Cc ${cc}` : "", bcc ? `Bcc ${bcc}` : ""].filter(Boolean).join(" · ");
}

/**
 * "Sending to Noor as Vovik with 2 files" or "Forwarding to ops@…, cfo@… · Cc 1". `as` is the
 * sending mailbox's first name, where it is known: the one moment the rep reliably looks.
 */
function holdWords(send: Send, name: string, as: string | null): { lead: string; spoken: string } {
  const count = send.files?.length ?? 0;
  const carried = count === 0 ? "" : count === 1 ? " with 1 file" : ` with ${count} files`;
  if (sendKind(send) === "forward") {
    const audience = forwardAudience(send);
    return {
      lead: `Forwarding to ${audience}${carried}`,
      spoken: `Your forward to ${audience}${carried}`,
    };
  }
  const from = as ? ` as ${as}` : "";
  return {
    lead: `Sending to ${name}${from}${carried}`,
    spoken: `Your reply to ${name}${from}${carried}`,
  };
}

/**
 * The held send: its countdown drawn (and hidden from assistive technology, which hears one fixed
 * sentence instead of a number a second), and Undo while the server allows it.
 */
export function HoldRow(props: {
  send: Send;
  name: string;
  busy: boolean;
  onUndo: () => void;
  /** Send was pressed here and its button went: the hold takes the focus, so Tab reaches Undo next. */
  grab: boolean;
  /** Whom the reply signs as, where the box knows the sending mailbox's name. */
  as?: string | null;
}) {
  const { send, grab } = props;
  const seconds = useSecondsLeft(send.holdUntil, true);
  const waiting = send.reason === "update_required";
  const words = holdWords(send, props.name, props.as ?? null);
  const row = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (grab && document.activeElement === document.body) row.current?.focus();
  }, [grab]);
  return (
    <div className="lcr-send-hold lcr-i2-hold" ref={row} tabIndex={-1}>
      <span aria-hidden="true">
        {waiting
          ? "Not sent yet: waiting for an app update to send its files"
          : seconds > 0
            ? `${words.lead} in ${seconds} s`
            : `${words.lead}…`}
      </span>
      <span className="lcr-visually-hidden" role="status">
        {waiting
          ? `${words.spoken} is not sent yet: it waits for an app update to send its files. Undo is available.`
          : `${words.spoken} is held for 10 seconds. Undo is available.`}
      </span>
      <button
        type="button"
        className="lcr-send-button"
        disabled={props.busy}
        onClick={props.onUndo}
      >
        Undo
      </button>
    </div>
  );
}

/** Under way, after the hold: which kind, to whom. */
export function claimedWords(send: Send, name: string): string {
  return sendKind(send) === "forward"
    ? `Forwarding to ${forwardAudience(send)}…`
    : `Sending your reply to ${name}…`;
}

function BisonLink(props: { url: string }) {
  return (
    <a className="lcr-reply-bison" href={props.url} target="_blank" rel="noreferrer">
      <ExternalLinkIcon size={16} /> Open in Bison
    </a>
  );
}

/** The next look, never a time already gone: a look overdue is about to happen. */
function nextLook(nextAt: string, now: number): string {
  return Date.parse(nextAt) <= now ? "shortly" : appDateTime(nextAt);
}

/**
 * An unknown send is looked for in Bison's Sent folder for up to seven days and confirmed only
 * from what is found (`send-reconciler.ts`); never sent again. Until then, or once looking stops,
 * the rep is told not to resend.
 */
function UncertainWords(props: { check: SendCheck | undefined; kind: "reply" | "forward" }) {
  const { check, kind } = props;
  if (check?.nextAt && check.stopped === null)
    return (
      <>
        <strong>Checking Bison, don't resend.</strong> LeadCue lost track of whether this {kind}{" "}
        went out and is looking for it in Bison's Sent folder:{" "}
        {check.checks === 0
          ? "first look"
          : `looked ${appCount(check.checks)} ${check.checks === 1 ? "time" : "times"}, next look`}{" "}
        {nextLook(check.nextAt, Date.now())}.
      </>
    );
  if (check?.stopped === "ambiguous")
    return (
      <>
        <strong>Check Bison, don't resend.</strong> Bison holds {appCount(check.candidates.length)}{" "}
        sent {kind === "forward" ? "forwards" : "replies"} that could be this one (
        {check.candidates.map((at) => appDateTime(at)).join("; ")}
        ). Look in Bison's Sent folder to see which went out.
      </>
    );
  if (check?.stopped)
    return (
      <>
        <strong>Check Bison, don't resend.</strong> LeadCue could not find this {kind} in Bison's
        Sent folder and has stopped looking. Look there before doing anything else.
      </>
    );
  return (
    <>
      <strong>Check Bison, don't resend.</strong> LeadCue lost track of whether this {kind} went
      out. Look in Bison's Sent folder before doing anything else.
    </>
  );
}

/** One block for a reply or a forward whose outcome is unknown: what to do, Bison, and settling. */
export function Uncertain(props: {
  send: Send | null | undefined;
  /** The kind when the status has not yet named the send (a request whose answer was lost). */
  kind: "reply" | "forward";
  bisonUrl: string | null;
  busy: boolean;
  onSettle: Settle;
  onShow?: (() => void) | null;
}) {
  const kind = props.send ? sendKind(props.send) : props.kind;
  const check = props.send?.state === "uncertain" ? props.send.check : undefined;
  return (
    <div className="lcr-send-status lcr-send-uncertain" role="alert">
      <p className="lcr-send-line">
        <UncertainWords check={check} kind={kind} />
      </p>
      <SettleSend check={check} kind={kind} busy={props.busy} onSettle={props.onSettle} />
      {(props.bisonUrl || props.onShow) && (
        <div className="lcr-i2-uncertain-actions">
          {props.bisonUrl && <BisonLink url={props.bisonUrl} />}
          {props.onShow && (
            <button type="button" className="lcr-ghost lcr-small-button" onClick={props.onShow}>
              Show what you wrote
            </button>
          )}
        </div>
      )}
    </div>
  );
}
